import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  sendSignInLinkToEmail, 
  isSignInWithEmailLink, 
  signInWithEmailLink,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  increment,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Firebase configuration
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// Email authentication settings
const actionCodeSettings = {
  // URL you want to redirect back to after sign-in
  url: window.location.origin,
  handleCodeInApp: true,
};

// Check if user exists by email
const checkUserExists = async (email: string): Promise<boolean> => {
  try {
    // Special case for Razorpay test account
    if (email.toLowerCase() === 'testervip@ut.com') {
      return true;
    }
    
    // Query Firestore for users with this email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking if user exists:', error);
    return false;
  }
};

// Send sign-in link to email
const sendSignInLink = async (email: string): Promise<void> => {
  try {
    // Special case for Razorpay test account
    if (email.toLowerCase() === 'testervip@ut.com') {
      // For test account, we'll simulate a successful login without actually sending an email
      localStorage.setItem('testAccountLogin', 'true');
      return Promise.resolve();
    }
    
    // Check if user exists
    const userExists = await checkUserExists(email);
    
    // Store whether this is a login or signup
    localStorage.setItem('isExistingUser', userExists ? 'true' : 'false');
    
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    // Save the email locally to use it for sign-in
    localStorage.setItem('emailForSignIn', email);
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
};

// Check if the URL contains sign-in link
const checkSignInLink = (): boolean => {
  // Special case for Razorpay test account
  if (localStorage.getItem('testAccountLogin') === 'true') {
    return true;
  }
  
  return isSignInWithEmailLink(auth, window.location.href);
};

// Sign in with email link
const signInWithEmail = async (): Promise<any> => {
  try {
    // Special case for Razorpay test account
    if (localStorage.getItem('testAccountLogin') === 'true') {
      localStorage.removeItem('testAccountLogin');
      
      try {
        // Try to sign in with email and password for test account
        const result = await signInWithEmailAndPassword(auth, 'testervip@ut.com', '1591955');
        
        // If the user doesn't exist yet, create it
        if (result.user) {
          const userRef = doc(db, 'users', result.user.uid);
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            // Create new user document for test account
            await setDoc(userRef, {
              email: 'testervip@ut.com',
              name: 'Razorpay Tester',
              phoneNumber: '+911234567890',
              country: 'India',
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              usageCount: 0,
              subscription: 'premium', // Give premium access to test account
              subscriptionExpiry: null
            });
          } else {
            // Update existing user
            await updateDoc(userRef, {
              lastLogin: serverTimestamp(),
              subscription: 'premium' // Ensure test account always has premium
            });
          }
        }
        
        return result;
      } catch (error) {
        console.error('Error signing in test account:', error);
        // If sign in fails, create a custom user object
        return {
          user: {
            uid: 'testervip-uid',
            email: 'testervip@ut.com',
            displayName: 'Razorpay Tester'
          }
        };
      }
    }
    
    // Get the email from localStorage
    let email = localStorage.getItem('emailForSignIn');
    
    if (!email) {
      // If missing email, prompt user for it
      email = window.prompt('Please provide your email for confirmation');
    }
    
    if (!email) {
      return Promise.reject(new Error('Email is required'));
    }
    
    // Sign in with email link
    const result = await signInWithEmailLink(auth, email, window.location.href);
    
    // Clear email from storage
    localStorage.removeItem('emailForSignIn');
    
    // Clear the isExistingUser flag
    localStorage.removeItem('isExistingUser');
    
    // Create or update user in Firestore
    if (result.user) {
      const userRef = doc(db, 'users', result.user.uid);
      const userSnap = await getDoc(userRef);
      
      // Get user data from localStorage if available
      let userData = null;
      try {
        const storedData = localStorage.getItem('userSignupData');
        if (storedData) {
          userData = JSON.parse(storedData);
          // Clear the data after retrieving it
          localStorage.removeItem('userSignupData');
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
      
      if (!userSnap.exists()) {
        // Create new user document
        await setDoc(userRef, {
          email: result.user.email,
          name: userData?.name || '',
          phoneNumber: userData?.phoneNumber || '',
          country: userData?.country || '',
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          usageCount: 0,
          subscription: 'free',
          subscriptionExpiry: null
        });
      } else {
        // Update existing user
        await updateDoc(userRef, {
          lastLogin: serverTimestamp(),
          // Update user data if provided and not already set
          ...(userData?.name && !userSnap.data().name ? { name: userData.name } : {}),
          ...(userData?.phoneNumber && !userSnap.data().phoneNumber ? { phoneNumber: userData.phoneNumber } : {}),
          ...(userData?.country && !userSnap.data().country ? { country: userData.country } : {})
        });
      }
    }
    
    return result;
  } catch (error) {
    return Promise.reject(error);
  }
};

// Get current user data from Firestore
const getUserData = async (userId: string) => {
  try {
    // Special case for Razorpay test account
    if (userId === 'testervip-uid') {
      return {
        email: 'testervip@ut.com',
        name: 'Razorpay Tester',
        subscription: 'premium',
        usageCount: 0
      };
    }
    
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting user data:", error);
    return null;
  }
};

// Update user usage count
const updateUserUsage = async (userId: string) => {
  try {
    // Skip for test account
    if (userId === 'testervip-uid') {
      return;
    }
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      usageCount: increment(1),
      lastUsage: serverTimestamp()
    });
  } catch (error) {
    console.error("Error updating user usage:", error);
  }
};

// Check if a processed video exists in cache
const checkVideoCache = async (videoId: string, category: string) => {
  try {
    const cacheRef = doc(db, 'videoCache', videoId);
    const cacheSnap = await getDoc(cacheRef);
    
    if (cacheSnap.exists() && cacheSnap.data().category === category) {
      // Check if cache is still valid (not expired)
      const cacheData = cacheSnap.data();
      const cacheTime = cacheData.updatedAt.toDate();
      const now = new Date();
      
      // Cache valid for 7 days
      const cacheValidDays = 7;
      const cacheValidUntil = new Date(cacheTime);
      cacheValidUntil.setDate(cacheValidUntil.getDate() + cacheValidDays);
      
      if (now < cacheValidUntil) {
        return cacheData;
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error checking video cache:", error);
    return null;
  }
};

// Save processed video to cache
const saveVideoCache = async (videoId: string, category: string, data: any) => {
  try {
    const cacheRef = doc(db, 'videoCache', videoId);
    await setDoc(cacheRef, {
      videoId,
      category,
      data,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error saving video cache:", error);
  }
};

// Get user subscription status
const getUserSubscription = async (userId: string) => {
  try {
    // Special case for Razorpay test account
    if (userId === 'testervip-uid') {
      return { type: 'premium', expiry: null };
    }
    
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      return {
        type: userData.subscription || 'free',
        expiry: userData.subscriptionExpiry ? userData.subscriptionExpiry.toDate() : null
      };
    }
    
    return { type: 'free', expiry: null };
  } catch (error) {
    console.error("Error getting user subscription:", error);
    return { type: 'free', expiry: null };
  }
};

// Check if user has reached usage limit
const checkUsageLimit = async (userId: string) => {
  try {
    // Special case for Razorpay test account - unlimited usage
    if (userId === 'testervip-uid') {
      return true;
    }
    
    const userData = await getUserData(userId);
    
    if (!userData) return false;
    
    const subscription = userData.subscription || 'free';
    const usageCount = userData.usageCount || 0;
    
    // Define usage limits based on subscription
    const limits = {
      free: 5,
      basic: 50,
      premium: 200
    };
    
    // Get limit for user's subscription
    const userLimit = limits[subscription as keyof typeof limits] || limits.free;
    
    return usageCount < userLimit;
  } catch (error) {
    console.error("Error checking usage limit:", error);
    return false;
  }
};

// Setup automated daily backups
// This is a placeholder - actual implementation would be in Firebase Cloud Functions
const setupAutomatedBackups = () => {
  console.log("Automated backups would be set up in Firebase Cloud Functions");
};

// Restore user's capsule history
const restoreUserCapsules = async (userId: string, backupId: string) => {
  try {
    // This is a placeholder for the actual implementation
    console.log(`Restoring capsules for user ${userId} from backup ${backupId}`);
    
    // In a real implementation, this would restore from a backup collection
    return true;
  } catch (error) {
    console.error("Error restoring user capsules:", error);
    return false;
  }
};

export {
  auth,
  db,
  storage,
  sendSignInLink,
  checkSignInLink,
  signInWithEmail,
  getUserData,
  updateUserUsage,
  checkVideoCache,
  saveVideoCache,
  getUserSubscription,
  checkUsageLimit,
  setupAutomatedBackups,
  restoreUserCapsules,
  checkUserExists
};
